import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import {
  Trash2,
  Edit,
  Plus,
  Printer,
  X,
  Lightbulb,
  Loader2,
  Check,
} from "lucide-react";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  allItems = [],
  onSelectItem,
  type = "default",
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Initialize search term only once when component mounts or value changes
  useEffect(() => {
    if (!hasSelected) {
      if (value) {
        if (type === "itemSearch") {
          const selectedItem = allItems.find(item => item.ItemID === value);
          if (selectedItem) {
            setSearchTerm(selectedItem.ItemName);
          }
        } else {
          const selectedOption = options.find(opt => opt.value === value);
          if (selectedOption) {
            setSearchTerm(selectedOption.label);
          }
        }
      }
    }
  }, [value, options, allItems, type, hasSelected]);

  const filteredOptions = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    if (type === "itemSearch") {
      return allItems.filter(
        item => item && (
          item.ItemName.toLowerCase().includes(searchLower) ||
          item.ItemID.toString().toLowerCase().includes(searchLower)
        )
      );
    }
    return options.filter(
      opt => opt && (
        opt.label.toLowerCase().includes(searchLower) ||
        opt.value.toString().includes(searchLower)
      )
    );
  }, [options, searchTerm, type, allItems]);

  const handleSelectOption = useCallback((option) => {
    if (type === "itemSearch") {
      onSelectItem && onSelectItem(option);
      setSearchTerm(option ? option.ItemName : '');
    } else {
      onChange(option ? option.value : '');
      setSearchTerm(option ? option.label : '');
    }
    setHasSelected(true);
    setIsOpen(false);
    setSelectedIndex(-1);
  }, [onChange, onSelectItem, type]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
        return;
      }
    }

    if (filteredOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
          listRef.current?.children[newIndex]?.scrollIntoView({ block: "nearest" });
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
          listRef.current?.children[newIndex]?.scrollIntoView({ block: "nearest" });
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  }, [isOpen, filteredOptions, selectedIndex, handleSelectOption]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setSelectedIndex(-1);
    setHasSelected(false);  // Reset selection state when typing
  }, []);

  const clearSelection = useCallback(() => {
    setSearchTerm('');
    setHasSelected(false);
    if (type === "itemSearch") {
      onSelectItem && onSelectItem(null);
    } else {
      onChange('');
    }
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  }, [onChange, onSelectItem, type]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
        
        // Only reset search term if there was a previous selection
        if (value && !hasSelected) {
          if (type === "itemSearch") {
            const selectedItem = allItems.find(item => item.ItemID === value);
            if (selectedItem) {
              setSearchTerm(selectedItem.ItemName);
            }
          } else {
            const selectedOption = options.find(opt => opt.value === value);
            if (selectedOption) {
              setSearchTerm(selectedOption.label);
            }
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options, allItems, type, hasSelected]);

  return (
    <div className="relative mb-4" ref={dropdownRef}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 
            ${disabled ? "bg-gray-100" : ""} 
            ${!disabled && isOpen ? "rounded-b-none" : ""}`}
          placeholder={`Search ${placeholder}`}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setIsOpen(true)}
          disabled={disabled}
        />
        {searchTerm && !disabled && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={clearSelection}
            type="button"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && !disabled && (
        <div
          ref={listRef}
          className="absolute z-50 w-full max-h-60 overflow-auto bg-white border-x border-b rounded-b shadow-lg"
        >
          {filteredOptions.map((option, index) => (
            <div
              key={type === "itemSearch" ? option.ItemID : option.value}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                index === selectedIndex ? "bg-blue-50 text-blue-600" : ""
              }`}
              onClick={() => handleSelectOption(option)}
            >
              <div className="flex justify-between items-center">
                <span>
                  {type === "itemSearch" ? option.ItemName : option.label}
                </span>
                <span className="text-gray-400 text-sm">
                  ID: {type === "itemSearch" ? option.ItemID : option.value}
                </span>
              </div>
              {type === "itemSearch" && option.CategoryName && (
                <div className="text-sm text-gray-500">
                  Category: {option.CategoryName}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const Modal = ({ onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white rounded-lg p-6 w-[800px] max-h-[80vh] overflow-y-auto relative">
      <button
        className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
        onClick={onClose}
      >
        <X size={24} />
      </button>
      {children}
    </div>
  </div>
);

const calculatePromisedDate = (normalDays) => {
  const date = new Date();
  date.setDate(date.getDate() + normalDays);
  return date.toISOString().split("T")[0];
};

const L1ItemsTable = ({
  indentID,
  onDeleteItem,
  onUpdateItem,
  isDraft,
  isEditable = true,
  onSubmit,
  onSaveDraft,
  onPrint,
  indentStatus = { l1a: 0, l1: 0 },
  setPageLoading,
}) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState(null);

  const [tableData, setTableData] = useState({
    categories: [],
    allItems: [],
    items: [],
    accountingUnits: [],
  });

  const [uiState, setUiState] = useState({
    modalOpen: false,
    loading: false,
    error: null,
    isSubmitting: false,
  });

  const [editingItem, setEditingItem] = useState(null);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });

  const initialNewItem = {
    categoryID: "",
    itemID: "",
    quantity: 1,
    stock: 0,
    remarks: "",
    normalDays: 0,
    promisedDate: "",
    accountingUnitID: "",
    accountingUnitName: "",
  };
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [newItem, setNewItem] = useState(initialNewItem);
  const [items, setItems] = useState([]);
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [itemsUpdated, setItemsUpdated] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Add useEffect to pre-fetch descriptions for all items
  useEffect(() => {
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.itemID && !itemDescriptions[item.itemID]) {
          fetchItemDescription(item.itemID);
        }
      });
    }
  }, [items]);

  const formatQuantity = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, "");
    const parts = cleanValue.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join("");
    }
    if (parts[1]?.length > 3) {
      return parts[0] + "." + parts[1].slice(0, 3);
    }
    return cleanValue;
  };

  const handleQuantityChange = (value, setterFunction) => {
    const formattedValue = formatQuantity(value);
    setterFunction(formattedValue);
  };

  const fetchItems = useCallback(async () => {
    if (!indentID) return;

    setUiState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => ({
          indentItemID: item.IndentItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          itemID: item.ItemID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          normalDays: item.NormalDays,
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName,
        }));

      setTableData((prev) => ({ ...prev, items: fetchedItems }));
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to load items. Please try again.",
      }));
      console.error("Error fetching items:", error);
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  }, [indentID]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setUiState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const [categoryRes, itemRes, accountingUnitsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/dropdown/mcatdrop`),
          axios.get(`${API_BASE_URL}/dropdown/mitemdrop`),
          axios.get(`${API_BASE_URL}/dropdown/maudrop`),
        ]);

        setCategories(categoryRes.data || []);
        setAllItems(itemRes.data || []);
        setTableData((prev) => ({
          ...prev,
          categories: categoryRes.data || [],
          allItems: itemRes.data || [],
          accountingUnits: accountingUnitsRes.data || [],
        }));

        await fetchItems();
      } catch (error) {
        setUiState((prev) => ({
          ...prev,
          error: "Failed to load initial data. Please refresh the page.",
        }));
        console.error("Error fetching initial data:", error);
      } finally {
        setUiState((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchInitialData();
  }, [fetchItems]);

  const handleItemChange = useCallback(
    (itemId) => {
      const parsedItemId = parseInt(itemId, 10);
      const selectedItem = filteredItems.find(
        (item) => item.ItemID === parsedItemId
      );

      if (selectedItem) {
        setNewItem((prev) => ({
          ...prev,
          itemID: parsedItemId,
          accountingUnitID: selectedItem.AccountingUnitID || "",
          accountingUnitName: selectedItem.UnitName || "",
          stock: selectedItem.Stock || 0,
        }));
      }
    },
    [filteredItems]
  );

  const handleCategoryChange = (categoryId) => {
    if (!categoryId) {
      setNewItem((prev) => ({
        ...prev,
        categoryID: "",
        itemID: "",
        normalDays: 0,
        promisedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      }));
      setFilteredItems([]);
      return;
    }

    const parsedCategoryId = parseInt(categoryId, 10);
    const selectedCategory = tableData.categories.find(
      (cat) => cat.CategoryID === parsedCategoryId
    );

    if (selectedCategory) {
      const normalDays = selectedCategory.NormalDays || 0;
      const promisedDate = calculatePromisedDate(normalDays);

      setNewItem((prev) => ({
        ...prev,
        categoryID: parsedCategoryId,
        itemID: "",
        normalDays: normalDays,
        promisedDate: promisedDate,
        accountingUnitID: "",
        accountingUnitName: "",
      }));

      const filtered = tableData.allItems.filter(
        (item) => item.CategoryID === parsedCategoryId
      );
      setFilteredItems(filtered);
    }
  };

  const handleNewItemFieldChange = (field, value) => {
    if (field === "quantity") {
      // Allow up to 3 decimal places for quantity
      const formattedValue = parseFloat(value).toFixed(3);
      setNewItem((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else if (field === "normalDays") {
      const promisedDate = calculatePromisedDate(value);
      setNewItem((prev) => ({
        ...prev,
        [field]: value,
        promisedDate: promisedDate,
      }));
    } else {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleAddNewItem = async () => {
    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    if (!newItem.itemID || !newItem.categoryID) {
      setUiState((prev) => ({
        ...prev,
        error: "Please select both category and item",
      }));
      return;
    }

    setUiState((prev) => ({ ...prev, loading: true, error: null }));
    setPageLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/l1page/indentitems`, {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: parseFloat(newItem.quantity).toFixed(3),
        Description: newItem.remarks || "",
        AccountingUnitID: newItem.accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
      });

      await fetchItems();
      setNewItem(initialNewItem);
      setUiState((prev) => ({ ...prev, modalOpen: false, error: null }));

      setChangeLog((prev) => ({
        ...prev,
        addedItems: [...prev.addedItems, response.data],
      }));

      onUpdateItem?.({
        type: "add",
        item: response.data,
        changeLog,
      });
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to add item. Please try again.",
      }));
      console.error("Error adding item:", error);
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
      setPageLoading(false);
    }
  };

  const handleDeleteItem = async (index) => {
    const itemToDelete = tableData.items[index];

    if (!itemToDelete || !itemToDelete.itemID) {
      setUiState((prev) => ({
        ...prev,
        error: "Invalid item data for deletion",
      }));
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/l1page/indentitems/${itemToDelete.itemID}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            indentID: indentID,
          },
        }
      );

      setTableData((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));

      const deletedItemRecord = {
        itemID: itemToDelete.itemID,
        indentItemID: itemToDelete.indentItemID,
        itemName: itemToDelete.itemName,
        quantity: itemToDelete.quantity,
        stock: itemToDelete.stock,
        categoryID: itemToDelete.categoryID,
        remarks: itemToDelete.remarks,
        promisedDate: itemToDelete.promisedDate,
        accountingUnitID: itemToDelete.accountingUnitID,
        unitname: itemToDelete.unitname,
        deletedAt: new Date().toISOString(),
      };

      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, deletedItemRecord],
      }));

      onUpdateItem?.({
        type: "delete",
        item: deletedItemRecord,
        changeLog: {
          deletedItems: [deletedItemRecord],
        },
      });

      onDeleteItem?.(index);

      setUiState((prev) => ({
        ...prev,
        error: null,
      }));
    } catch (error) {
      console.error("Error deleting item:", error);
      let errorMessage = "Failed to delete item. Please try again.";

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = "Missing required information for deletion.";
            break;
          case 401:
            errorMessage = "Session expired. Please log in again.";
            break;
          case 403:
            errorMessage = "You don't have permission to delete this item.";
            break;
          case 404:
            errorMessage =
              "Item not found. Please refresh the page and try again.";
            break;
          case 500:
            errorMessage =
              "Server error while deleting item. Please try again.";
            break;
          default:
            errorMessage =
              error.response.data?.error ||
              "Failed to delete item. Please try again.";
        }
      }

      setUiState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    }
  };


  const handleUpdateDescription = async (itemId, newDescription) => {
    try {
      await axios.put(`${API_BASE_URL}/viewTable/itemdescription/${itemId}`, {
        Description: newDescription,
        IndentID: indentID,
      });

      setItemDescriptions((prev) => ({
        ...prev,
        [itemId]: newDescription,
      }));

      setEditingItem((prev) => ({
        ...prev,
        description: newDescription,
        isEditingDescription: false,
      }));
    } catch (error) {
      console.error("Error updating description:", error);
      setError("Failed to update description. Please try again.");
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;

    setUiState((prev) => ({ ...prev, loading: true }));
    const formattedQuantity = parseFloat(editingItem.quantity).toFixed(3);

    try {
      await axios.put(
        `${API_BASE_URL}/l1page/indentitems/${editingItem.indentItemID}`,
        {
          Quantity: formattedQuantity,
          Stock: editingItem.stock,
          Description: editingItem.description, // Add description to the update
        }
      );
      setItemDescriptions((prev) => ({
        ...prev,
        [editingItem.itemID]: editingItem.description,
      }));

      await fetchItems();
      const updatedItems = tableData.items.map((item) =>
        item.indentItemID === editingItem.indentItemID
          ? {
              ...item,
              quantity: formattedQuantity,
              stock: editingItem.stock,
              remarks: editingItem.description, // Update remarks in items array
            }
          : item
      );

      setTableData((prev) => ({ ...prev, items: updatedItems }));
      setEditingItem(null);

      onUpdateItem?.({
        type: "update",
        item: editingItem,
        items: updatedItems,
      });
    } catch (error) {
      console.error("Error updating item:", error);
      setUiState((prev) => ({
        ...prev,
        error: "Failed to update item. Please try again.",
      }));
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  };

  const refreshItemsData = async () => {
    if (!indentID) return;

    setIsRefreshing(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => ({
          indentItemID: item.IndentItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          itemID: item.ItemID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          normalDays: item.NormalDays,
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName, // Make sure this is included
        }));

      setItems(fetchedItems);
    } catch (error) {
      console.error("Error refreshing items:", error);
      setError("Failed to refresh items. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (indentID) {
      refreshItemsData();
    }
  }, [indentID]);
  // Refresh items data after updates
  useEffect(() => {
    const refreshItems = async () => {
      if (itemsUpdated && indentID) {
        try {
          console.log("Refreshing items data...");
          const response = await axios.get(
            `${API_BASE_URL}/viewTable/indentitems`,
            {
              params: { indentId: indentID },
            }
          );

          const fetchedItems = response.data
            .filter((item) => item.IndentID === indentID)
            .map((item) => ({
              indentItemID: item.IndentItemID,
              itemName: item.ItemName,
              quantity: item.Quantity,
              stock: item.Stock || 0,
              categoryID: item.CategoryID,
              itemID: item.ItemID,
              remarks: item.Description,
              promisedDate: item.PromisedDate,
              normalDays: item.NormalDays,
              accountingUnitID: item.AccountingUnitID,
              unitname: item.UnitName,
            }));

          console.log("Refreshed items data:", fetchedItems);
          setItems(fetchedItems);
          setItemsUpdated(false);
        } catch (error) {
          console.error("Error refreshing items:", error);
        }
      }
    };

    refreshItems();
  }, [itemsUpdated, indentID]);

  const handleFinalSubmit = async () => {
    setUiState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      await onSubmit?.();
      setChangeLog({
        addedItems: [],
        deletedItems: [],
        updatedItems: [],
      });
    } catch (error) {
      console.error("Error submitting:", error);
      setUiState((prev) => ({
        ...prev,
        error: "Failed to submit. Please try again.",
      }));
    } finally {
      setUiState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleItemSearch = useCallback(
    (selectedItem) => {
      // Handle null/clearing case
      if (!selectedItem) {
        setFilteredItems([]);
        setNewItem((prev) => ({
          ...prev,
          categoryID: "",
          itemID: "",
          accountingUnitID: "",
          accountingUnitName: "",
          stock: 0,
          normalDays: 0,
          promisedDate: "",
          commitedDate: "",
        }));
        return;
      }

      // First set filtered items for the selected category
      const categoryItems = allItems.filter(
        (item) => item.CategoryID === selectedItem.CategoryID
      );
      setFilteredItems(categoryItems);

      // Get the normalDays from the category
      const selectedCategory = categories.find(
        (cat) => cat.CategoryID === selectedItem.CategoryID
      );
      const normalDays = selectedCategory?.NormalDays || 0;

      // Calculate dates
      const currentDate = new Date();
      const promisedDate = new Date(currentDate);
      promisedDate.setDate(currentDate.getDate() + normalDays);

      // Update all state in one go
      setNewItem((prev) => ({
        ...prev,
        categoryID: selectedItem.CategoryID,
        itemID: selectedItem.ItemID,
        accountingUnitID: selectedItem.AccountingUnitID || "",
        accountingUnitName: selectedItem.UnitName || "",
        stock: selectedItem.Stock || 0,
        normalDays: normalDays,
        promisedDate: promisedDate.toISOString().split("T")[0],
        commitedDate: promisedDate.toISOString().split("T")[0],
      }));
    },
    [allItems, categories]
  );

  const isSubmittedStage = useMemo(
    () => indentStatus.l1a === 1 && indentStatus.l1 === 1,
    [indentStatus]
  );

  const renderDescriptionCell = (itemId) => {
    if (editingItem?.itemID === itemId) {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="text"
            className="border rounded px-2 py-1 text-sm w-32"
            value={editingItem.description}
            onChange={(e) =>
              setEditingItem((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
        </div>
      );
    }
  
    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }
  
    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${
              description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
            } transition-colors duration-200`}
            onClick={() => {
              if (!description) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  };

  const handleEditItem = (item, index) => {
    // Ensure we fetch the description if it hasn't been loaded yet
    if (!itemDescriptions[item.itemID]) {
      fetchItemDescription(item.itemID);
    }
    
    setEditingItem({
      ...item,
      index: index,
      indentItemID: item.indentItemID,
      description: itemDescriptions[item.itemID] || "",
    });
    setItemsUpdated(true);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
      {uiState.loading && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
          <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
        </div>
      )}

      {uiState.error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {uiState.error}
        </div>
      )}

      <div className="flex justify-between items-center border-b pb-4 mb-4">
        <h2 className="text-xl font-bold text-gray-800">Invoice Items</h2>

        <div className="flex items-center space-x-2">
          <button
            className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
            onClick={onPrint}
            title="Print Invoice"
          >
            <Printer size={20} />
          </button>

          {!isSubmittedStage && isDraft && isEditable && (
            <button
              className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors duration-300"
              onClick={() =>
                setUiState((prev) => ({ ...prev, modalOpen: true }))
              }
              title="Add Item"
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-center w-8"></th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Normal Days</th>
              {!isSubmittedStage && (
                <th className="px-4 py-3 text-center w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.items.length > 0 ? (
              tableData.items.map((item, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 font-medium">{item.itemName}</td>
                  <td className="px-4 py-3 text-center">
                    {renderDescriptionCell(item.itemID)}
                  </td>
                  <td className="px-4 py-3 font-medium">{item.unitname}</td>

                  {editingItem && editingItem.index === index ? (
                    <>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          className="w-full border rounded px-2 py-1"
                          value={editingItem.quantity}
                          onChange={(e) =>
                            handleQuantityChange(e.target.value, (value) =>
                              setEditingItem((prev) => ({
                                ...prev,
                                quantity: value,
                              }))
                            )
                          }
                          placeholder="0.000"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1"
                          value={editingItem.stock}
                          onChange={(e) =>
                            setEditingItem((prev) => ({
                              ...prev,
                              stock: parseFloat(e.target.value),
                            }))
                          }
                          min="0"
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.normalDays}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            className="text-green-500 hover:text-green-700"
                            onClick={handleUpdateItem}
                          >
                            Save
                          </button>
                          <button
                            className="text-gray-500 hover:text-gray-700"
                            onClick={() => setEditingItem(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-center">
                        {parseFloat(item.quantity).toFixed(3)}
                      </td>
                      <td className="px-4 py-3 text-right">{item.stock}</td>
                      <td className="px-4 py-3 text-right">
                        {item.normalDays}
                      </td>
                      {!isSubmittedStage && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center space-x-2">
                            {isDraft && isEditable && (
                              <>
                                <button
                                  className="text-blue-500 hover:text-blue-700"
                                  onClick={() => handleEditItem(item, index)}
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteItem(index)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={!isSubmittedStage ? 7 : 6}
                  className="text-center text-gray-500 py-6"
                >
                  No items added to the invoice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-between items-center">
        <div className="flex space-x-4">
          {isSubmittedStage ? (
            <button
              className="bg-green-600 text-white px-5 py-2 rounded-md cursor-not-allowed opacity-50"
              disabled
            >
              Submitted
            </button>
          ) : (
            <>
              {isDraft && isEditable && (
                <>
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-md transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={onSaveDraft}
                    disabled={
                      tableData.items.length === 0 ||
                      !isDraft ||
                      uiState.isSubmitting
                    }
                  >
                    Save Draft
                  </button>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    onClick={handleFinalSubmit}
                    disabled={
                      tableData.items.length === 0 ||
                      !isDraft ||
                      uiState.isSubmitting
                    }
                  >
                    {uiState.isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <span>Submit Invoice</span>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {uiState.modalOpen && (
        <Modal
          onClose={() => {
            setUiState((prev) => ({ ...prev, modalOpen: false, error: null }));
          }}
        >
          <div className="flex">
            <div className="w-1/2 pr-4">
              {uiState.error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {uiState.error}
                </div>
              )}
              <h2 className="text-xl font-bold mb-4">Add New Item</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddNewItem();
                }}
              >
                <SearchableSelect
                  type="itemSearch"
                  options={[]}
                  allItems={allItems.map((item) => ({
                    ...item,
                    CategoryName: categories.find(
                      (cat) => cat.CategoryID === item.CategoryID
                    )?.CategoryName,
                  }))}
                  value={newItem.itemID}
                  onChange={() => {}}
                  onSelectItem={handleItemSearch}
                  placeholder="items"
                  label="Search Items"
                />

                <SearchableSelect
                  options={categories.map((cat) => ({
                    value: cat.CategoryID,
                    label: cat.CategoryName,
                  }))}
                  value={newItem.categoryID}
                  onChange={(value) => handleCategoryChange(value)}
                  placeholder="categories"
                  label="Category"
                />

                {newItem.categoryID && (
                  <SearchableSelect
                    options={filteredItems.map((item) => ({
                      value: item.ItemID,
                      label: item.ItemName,
                    }))}
                    value={newItem.itemID}
                    onChange={(value) => handleItemChange(value)}
                    placeholder="items"
                    label="Item"
                  />
                )}
                {/* Rest of your form remains the same */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.quantity}
                    onChange={(e) =>
                      handleQuantityChange(e.target.value, (value) =>
                        setNewItem((prev) => ({ ...prev, quantity: value }))
                      )
                    }
                    placeholder="Enter quantity (up to 3 decimal places)"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium">Stock</label>
                  <input
                    type="number"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.stock}
                    onChange={(e) =>
                      setNewItem((prev) => ({
                        ...prev,
                        stock: parseFloat(e.target.value),
                      }))
                    }
                    min="0"
                    required
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition-colors duration-300"
                  >
                    Add Item
                  </button>
                  <button
                    type="button"
                    className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded transition-colors duration-300"
                    onClick={() =>
                      setUiState((prev) => ({ ...prev, modalOpen: false }))
                    }
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>

            <div className="w-1/2 pl-4 border-l">
              <h3 className="text-lg font-semibold mb-4">Delivery Details</h3>
              {newItem.categoryID ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium">
                      Normal Days
                    </label>
                    <input
                      type="text"
                      className="border rounded w-full px-3 py-2 bg-gray-100"
                      value={newItem.normalDays || "Not selected"}
                      readOnly
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium">
                      Promised Date
                    </label>
                    <input
                      type="date"
                      className="border rounded w-full px-3 py-2 bg-gray-100"
                      value={newItem.promisedDate || ""}
                      readOnly
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium">
                      Accounting Unit
                    </label>
                    <input
                      type="text"
                      className="border rounded w-full px-3 py-2 bg-gray-100"
                      value={newItem.accountingUnitName || "Not selected"}
                      readOnly
                    />
                  </div>
                </>
              ) : (
                <p className="text-gray-500">
                  Select a category to view delivery details
                </p>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium">Remarks</label>
                <textarea
                  className="border rounded w-full px-3 py-2"
                  value={newItem.remarks}
                  onChange={(e) =>
                    setNewItem((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  placeholder="Optional remarks about the item"
                  rows="3"
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default L1ItemsTable;
